import { Injectable } from "@angular/core";
import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";

import { EnvironmentConfig } from "../models/environment-config.model";
import { GoogleAnalyticsService } from "ngx-google-analytics";
import { GoogleTagManagerService } from "angular-google-tag-manager";
import { HttpClient } from "@angular/common/http";
import { sha256 } from "js-sha256";
import { InventoryItem } from "../../inventory/inventory";
import { Cart, CartItem } from "../../cart/cart";

declare var environment: EnvironmentConfig;

export enum LogLevels {
  Trace = "Trace",
  Debug = "Debug",
  Info = "Info",
  Warn = "Warn",
  Error = "Error",
}

/** Browser SDK config that avoids duplicating server-side request telemetry. */
export function createBrowserAppInsightsConfig(instrumentationKey: string) {
  return {
    instrumentationKey,
    enableAutoRouteTracking: true,
    disableAjaxTracking: true,
    disableFetchTracking: true,
    correlationHeaderDomains: ["*.rubbl.com"],
  };
}

@Injectable({
  providedIn: "root",
})
export class LogService {
  private currentLogLevel: LogLevels;
  appInsights: ApplicationInsights;

  constructor(private gaService: GoogleAnalyticsService, private gtmService: GoogleTagManagerService, private httpClient: HttpClient) {
    this.currentLogLevel = (environment["logLevel"] as LogLevels) || LogLevels.Debug;

    this.appInsights = new ApplicationInsights({
      config: createBrowserAppInsightsConfig(environment.appInsightsKey),
    });

    this.appInsights.loadAppInsights();

    this.appInsights.addTelemetryInitializer(envelope => {
      const item = envelope.baseData;
      item.properties = item.properties || {};
      item.properties["ApplicationPlatform"] = "WEB";
      item.properties["ApplicationName"] = environment.clientId;
      envelope.tags["ai.cloud.role"] = environment.clientId;

      // since we are having app insights auto track the route it uses the window title for the event name
      // since our window title doesn't change right now, it will probably be dynamic in the future
      // lets just use the pathname
      if (envelope.baseType === "PageviewData") {
        item.name = window.location.pathname;
      }
    });

    this.appInsights.trackPageView(); // Manually call trackPageView to establish the current user/session/pageview
  }

  setUser(userId: string, accountId: string) {
    this.appInsights.setAuthenticatedUserContext(userId, accountId);
    this.gaService.gtag("set", { user_id: userId }); // Set the user ID using signed-in user_id.
  }

  clearUser() {
    this.appInsights.clearAuthenticatedUserContext();
  }

  trackEvent(name: string, properties?: { [key: string]: any }, sendToGtm: boolean = false) {
    this.appInsights.trackEvent({ name }, properties);

    if (sendToGtm) {
      this.gtmService.pushTag(properties);
    }
  }

  /**
   * Fire a single funnel/marketing event to Google Analytics (gtag), the Facebook
   * Conversions API, and App Insights in one call. Used by anonymous funnel pages
   * (e.g. the earnings-calculator landing page) that don't have a cart/ecommerce
   * shape but still need cost/attribution tracking across both ad platforms.
   *
   * `googleEventName` is the GA4 event name. `facebookEventName` lets callers map to
   * a Facebook standard event (e.g. "Lead", "PageView") where appropriate; when
   * omitted it falls back to the Google event name (sent as a Facebook custom event).
   */
  sendMarketingEvent(
    googleEventName: string,
    params?: { [key: string]: any },
    facebookEventName?: string,
    user?: any,
  ) {
    // App Insights
    this.trackEvent(googleEventName, params);

    // Google Analytics (gtag)
    try {
      if (window["gtag"]) {
        window["gtag"]("event", googleEventName, params || {});
      }
    } catch (e) {
      console.log(e);
    }

    // Facebook Conversions API
    try {
      this.facebookEvent(facebookEventName || googleEventName, params, user);
    } catch (e) {
      console.log(e);
    }
  }

  sendGoogleEcommerceView(
    event:
      | "view_item_list"
      | "view_item"
      | "add_to_cart_start_click"
      | "add_to_cart_attachments_modal_view"
      | "add_to_cart_dates_modal_view"
      | "add_to_cart_rental_details_modal_view",
    inventoryItem: InventoryItem[],
    item_list_id: string = "",
    item_list_name: string = "",
  ) {
    try {
      const items = inventoryItem.map((inventoryItem, index) => {
        return {
          item_Id: inventoryItem.id,
          index,
          item_name: `${inventoryItem.year} ${inventoryItem.make} ${inventoryItem.model} ${inventoryItem.serialNumber}`,
          item_brand: inventoryItem.make,
          item_category: inventoryItem.primaryType,
          item_list_id: item_list_id,
          item_list_name: item_list_name,
          price: inventoryItem.rentalRate,
          quantity: 1,
        };
      });
      window["gtag"]("event", event, {
        item_list_id,
        item_list_name,
        items,
      });
    } catch (e) {
      console.log(e);
    }
  }

  sendGoogleEcommerceEvent(
    event:
      | "add_to_cart"
      | "remove_from_cart"
      | "begin_checkout"
      | "add_payment_info"
      | "purchase"
      | "share_cart",
    cart: Cart,
  ) {
    try {
      const { items, value } = this.buildGa4ItemsAndValue(cart);

      let data: { [key: string]: any } = {
        currency: "USD",
        value,
        items,
      };

      if (event == "purchase") {
        data["transaction_id"] = cart.id;
        data["tax"] = cart.monthlySummary.Tax;
      }

      window["gtag"]("event", event, data);
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Pushes a GA4-format ecommerce event onto the GTM dataLayer.
   *
   * The production GA property is a (sunset) Universal Analytics property, so the direct
   * `window["gtag"]("event", ...)` calls above no longer reach reporting on their own — GA4
   * is fed through the GTM container (GTM-WD8K79F). Funnel events that must show up in GA4 /
   * Google Ads therefore have to be pushed to the dataLayer here, the same way the
   * checkout-step and purchase events already are (via `trackEvent(..., true)`).
   *
   * NOTE: this rides the SAME `event: "ecommerce"` custom-event envelope that the checkout-step
   * and purchase events already use (see checkout.service `logEvent`/`createOrder`), so no new
   * `begin_checkout` GTM trigger is required — it reuses the already-published trigger. A GA4
   * ecommerce tag on the `ecommerce` custom event must still be published in the container for
   * the event to be recorded.
   */
  sendGtmEcommerceEvent(event: "begin_checkout", cart: Cart) {
    try {
      const { items, value } = this.buildGa4ItemsAndValue(cart);

      // GA4 best practice: clear the previous ecommerce object before pushing a new one so
      // stale items don't leak between events.
      this.gtmService.pushTag({ ecommerce: null });

      // Ride the SAME `event: "ecommerce"` custom-event envelope that the checkout-step and
      // purchase events already use (see checkout.service `logEvent`/`createOrder`). That is the
      // trigger the published GTM container (GTM-WD8K79F) already listens for and forwards to GA4,
      // so this reaches GA4 WITHOUT needing a brand-new `begin_checkout` trigger/tag. We also send
      // the GA4-native `ecommerce` object so a GA4 ecommerce tag can read items/value.
      this.gtmService.pushTag({
        event: "ecommerce",
        ecommerceType: "checkout",
        ecommerceAction: event,
        ecommerce: {
          currency: "USD",
          value,
          items,
        },
      });
    } catch (e) {
      console.log(e);
    }
  }

  private buildGa4ItemsAndValue(cart: Cart): { items: any[]; value: number } {
    let value = 0;
    const items = (cart?.items || []).map((item, index) => {
      const price = (item.monthlySummary?.Rent || 0) + (item.monthlySummary?.PremiumServices || 0);
      value += price;
      return {
        item_id: item.inventoryItem?.id,
        item_name: `${item.inventoryItem?.year} ${item.inventoryItem?.make} ${item.inventoryItem?.model} ${item.inventoryItem?.serialNumber}`,
        index,
        item_brand: item.inventoryItem?.make,
        item_category: item.inventoryItem?.primaryType,
        price,
        quantity: 1,
      };
    });

    return { items, value };
  }

  // purchaseEvent = {
  //   data: [
  //     {
  //       event_name: "MachineCreated",
  //       event_time: 1703018326,
  //       action_source: "website",
  //       user_data: {
  //         em: [null],
  //         ph: [null],
  //         fbp: "fb.1.1703018665138.1394398981",
  //         fbc: "fb.1.1703019666868.IwAR0nW7x5FvuFapztLGiVSZmvndhdlBy1bHq-kVQlNmHUb0xCgxQCCguNvtM;",
  //       },
  //     },
  //   ],
  // };

  getFacebookUserData(user?: any) {
    const userData = {};
    if (user) {
      const email = user?.profile?.email.toLowerCase() || "";
      userData["em"] = sha256(email);
    }

    const cookieObject = this.parseCookie(document.cookie);
    if (cookieObject["_fbp"]) {
      userData["fbp"] = cookieObject["_fbp"];
    }

    if (cookieObject["_fbc"]) {
      userData["fbc"] = cookieObject["_fbc"];
    }

    return userData;
  }

  /**
   * Wrapper around `facebookEvent` for cart-shaped events. Builds the standard FB CAPI
   * `custom_data` payload (currency, value, content_ids, contents, num_items, optional
   * order_id) from a Cart or partial `{ items }` shape, so every funnel event sends a
   * consistent shape.
   *
   * Value is derived the same way `sendGoogleEcommerceEvent` derives it (rent + premium
   * services per item) so FB and Google report identical mid-funnel values. Purchase callers
   * can pass `opts.overrideValue` to send `cart.grandTotal` instead.
   */
  sendFacebookCartEvent(
    eventName: "AddToCart" | "InitiateCheckout" | "ShareCart" | "Purchase",
    cart: { items: CartItem[]; monthlySummary?: { Tax?: number } },
    user?: any,
    opts?: { orderId?: string; overrideValue?: number },
  ) {
    try {
      const customData = this.buildFacebookCartCustomData(cart, opts);
      this.facebookEvent(eventName, customData, user);
    } catch (e) {
      console.log(e);
    }
  }

  private buildFacebookCartCustomData(
    cart: { items: CartItem[]; monthlySummary?: { Tax?: number } },
    opts?: { orderId?: string; overrideValue?: number },
  ) {
    const items = cart?.items || [];

    let derivedValue = 0;
    const contents: { id: string; quantity: number; item_price: number }[] = [];
    const contentIds: string[] = [];

    for (const item of items) {
      const rent = item?.monthlySummary?.Rent || 0;
      const premium = item?.monthlySummary?.PremiumServices || 0;
      const itemPrice = rent + premium;
      derivedValue += itemPrice;

      const id = item?.inventoryItem?.id;
      if (id) {
        contentIds.push(id);
        contents.push({ id, quantity: 1, item_price: itemPrice });
      }
    }

    const value = opts?.overrideValue != null ? opts.overrideValue : derivedValue;

    const customData: {
      currency: string;
      value: number;
      content_ids: string[];
      content_type: "product";
      contents: { id: string; quantity: number; item_price: number }[];
      num_items: number;
      order_id?: string;
    } = {
      currency: "USD",
      value,
      content_ids: contentIds,
      content_type: "product",
      contents,
      num_items: items.length,
    };

    if (opts?.orderId) {
      customData.order_id = opts.orderId;
    }

    return customData;
  }

  /**
   * Generates a unique id for a single logical conversion event. Sending the SAME id from
   * both the browser Meta Pixel and this server-side Conversions API call is how Facebook
   * deduplicates the two so an action is only counted once. See:
   * https://developers.facebook.com/docs/marketing-api/conversions-api/deduplicate-pixel-and-server-events/
   */
  generateEventId(): string {
    const cryptoObj = typeof window !== "undefined" ? (window.crypto as any) : undefined;
    if (cryptoObj?.randomUUID) {
      return cryptoObj.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  }

  // for custom data https://developers.facebook.com/docs/marketing-api/conversions-api/using-the-api/
  facebookEvent(eventName: string, customData?: any, user?: any, userData?: any, eventId?: string) {
    const unixTimestamp = Math.floor(new Date().getTime() / 1000);

    let event = {
      event_name: eventName,
      event_time: unixTimestamp,
      // `event_id` lets Facebook deduplicate this server (CAPI) event against the matching
      // browser Pixel event. Without it, an action fired by both the Pixel and CAPI is
      // double-counted.
      event_id: eventId || this.generateEventId(),
      action_source: "website",
      user_data: userData != null ? userData : this.getFacebookUserData(user),
    };

    if (customData) {
      event["custom_data"] = customData;
    }

    if (environment?.production !== "true") {
      console.log(event);
      console.warn("Facebook event not sent in non-production environment");
      return;
    }

    this.httpClient.post(`${environment.adminApiDomain}/api/admin/v1/e/f`, { data: [event] }).subscribe(
      result => {},
      error => {
        // do nothing
      },
    );
  }

  parseCookie(cookieString) {
    const cookieObject = {};

    if (cookieString) {
      const cookiesArray = cookieString.split(";");
      cookiesArray.forEach(cookie => {
        const [key, value] = cookie.trim().split("=");
        cookieObject[key] = decodeURIComponent(value);
      });
    }

    return cookieObject;
  }

  pushTagToGtm(properties?: { [key: string]: any }) {
    // pushTag rejects when the GTM script fails. addGtmToDom rejects with `false`,
    // which Zone prints as Uncaught (in promise): [object Boolean].
    Promise.resolve(this.gtmService.pushTag(properties)).catch(() => undefined);
  }

  debug(componentOrServiceName: string, message: any) {
    if (this.currentLogLevel > LogLevels.Debug) {
      return;
    }

    console.log(`${componentOrServiceName}:`, message);

    this.appInsights.trackTrace({
      message: `${componentOrServiceName}: ${JSON.stringify(message)}`,
      severityLevel: SeverityLevel.Information,
    });
  }

  info(componentOrServiceName: string, message: any) {
    if (this.currentLogLevel > LogLevels.Info) {
      return;
    }

    console.log(`${componentOrServiceName}: ${JSON.stringify(message)}`);

    this.appInsights.trackTrace({
      message: `${componentOrServiceName}: ${JSON.stringify(message)}`,
      severityLevel: SeverityLevel.Information,
    });
  }

  warn(componentOrServiceName: string, message: any) {
    if (this.currentLogLevel > LogLevels.Warn) {
      return;
    }

    console.warn(`${componentOrServiceName}:`, message);
    this.appInsights.trackTrace({
      message: `${componentOrServiceName}: ${JSON.stringify(message)}`,
      severityLevel: SeverityLevel.Warning,
    });
  }

  error(componentOrServiceName: string, error: Error) {
    this.appInsights.trackException({ error });
    console.error(error);
  }
}
